// eslint-disable-next-line matrix-org/require-copyright-header
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParticipants } from "@livekit/components-react";
import {
  EventTimeline,
  EventType,
  MatrixEventEvent,
  RoomEvent as MatrixRoomEvent,
  type MatrixEvent,
  type Room as MatrixRoom,
} from "matrix-js-sdk";
import { logger } from "matrix-js-sdk/lib/logger";
import { ParticipantEvent, type Room } from "livekit-client";

import type {
  Participant,
  TrackPublication,
  RemoteTrack,
  RemoteTrackPublication,
} from "livekit-client";
import { useClient } from "../ClientContext.tsx";
import {
  RecordingChangeEventType,
  type RecordingChangeAction,
  type RecordingChangeEventContent,
  isRecordingChangeEventContent,
} from "./recordingChangeEvent.ts";

export type LocalRecordingState = "stopped" | "preparing" | "recording";

export type RecordingNotice =
  | { kind: "local_start"; sender: string | null }
  | { kind: "local_stop"; sender: string | null };

export type UseCallRecordingOptions = {
  onFinished?: (blob: Blob) => void;
  onRecordingNotice?: (notice: RecordingNotice) => void;
  mimeType?: string;
};

export type UseCallRecordingResult = {
  localRecordingState: LocalRecordingState;
  localRecordingUserIds: ReadonlySet<string>;
  isLocalRecording: boolean;
  isLocalPreparing: boolean;
  isAnyRecording: boolean;
  isUserLocallyRecording: (userId: string | null | undefined) => boolean;
  error: Error | null;
  startLocalRecording: () => Promise<void>;
  stopLocalRecording: () => Promise<void>;
};

type TrackSourceEntry = {
  source: MediaStreamAudioSourceNode;
  stream: MediaStream;
};

type ParticipantHandlers = {
  trackSubscribed: (track: RemoteTrack, pub: RemoteTrackPublication) => void;
  trackUnsubscribed: (track: RemoteTrack, pub: RemoteTrackPublication) => void;
  trackMuted: (pub: TrackPublication) => void;
  trackUnmuted: (pub: TrackPublication) => void;
};

type ActiveCallBoundaries = {
  callStartedAtTs: number | null;
  deviceJoinedAtTsByKey: Map<string, number>;
  activeDeviceIdsByUserId: Map<string, Set<string>>;
};

const CALL_MEMBERSHIP_EVENT_TYPE = EventType.GroupCallMemberPrefix;
const UNKNOWN_DEVICE_ID = "__legacy_recording_device__";
const DEVICE_KEY_SEPARATOR = "\u0000";
const EVENT_SCAN_PAGE_SIZE = 100;
const EVENT_SCAN_MAX_PAGES = 20;

const parseNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const parseCallMembershipCreatedTs = (event: MatrixEvent): number | null => {
  const content = event.getContent() as Record<string, unknown> | null;
  if (!content || typeof content !== "object") {
    return null;
  }

  if (Object.keys(content).length === 0) {
    // Пустое содержимое state-event означает leave для m.call.member.
    return null;
  }

  if (content.application !== "m.call") {
    return null;
  }

  if (typeof content.call_id !== "string") {
    return null;
  }

  if (content.scope !== undefined && content.scope !== "m.room") {
    return null;
  }

  const createdTs =
    typeof content.created_ts === "number" && Number.isFinite(content.created_ts)
      ? Math.floor(content.created_ts)
      : event.getTs();

  if (typeof content.expires === "number" && Number.isFinite(content.expires)) {
    if (createdTs + content.expires <= Date.now()) {
      return null;
    }
  }

  return createdTs;
};

const parseCallMembershipDeviceId = (event: MatrixEvent): string | null => {
  const content = event.getContent() as Record<string, unknown> | null;
  if (!content || typeof content !== "object") {
    // Продолжаем разбор через state_key: leave-ивенты приходят с пустым content.
  } else {
    const deviceId = parseNonEmptyString(content.device_id);
    if (deviceId) {
      return deviceId;
    }
  }

  const sender = event.getSender();
  const stateKey = parseNonEmptyString(event.getStateKey());
  if (!sender || !stateKey) {
    return null;
  }

  // Формат state_key: [_]{userId}_{deviceId}_m.call{callId}
  // Для room versions с ограниченными auth rules может быть ведущий "_".
  const normalizedStateKey = stateKey.startsWith("_")
    ? stateKey.slice(1)
    : stateKey;
  const senderPrefix = `${sender}_`;
  if (!normalizedStateKey.startsWith(senderPrefix)) {
    return null;
  }

  const callMarkerIndex = normalizedStateKey.lastIndexOf("_m.call");
  if (callMarkerIndex <= senderPrefix.length) {
    return null;
  }

  const deviceId = normalizedStateKey.slice(senderPrefix.length, callMarkerIndex);
  return deviceId.length > 0 ? deviceId : null;
};

const makeUserDeviceKey = (userId: string, deviceId: string): string =>
  `${userId}${DEVICE_KEY_SEPARATOR}${deviceId}`;

const parseUserIdFromDeviceKey = (key: string): string => {
  const separatorIndex = key.indexOf(DEVICE_KEY_SEPARATOR);
  return separatorIndex >= 0 ? key.slice(0, separatorIndex) : key;
};

const getUserIdFromParticipantIdentity = (identity: string | undefined): string | null => {
  if (!identity) {
    return null;
  }

  const separatorIndex = identity.lastIndexOf(":");
  if (separatorIndex < 0) {
    return null;
  }

  const userId = identity.slice(0, separatorIndex);
  return userId.startsWith("@") ? userId : null;
};

/**
 * Хук управления локальной записью звонка.
 */
export function useCallRecording(
  room: Room,
  matrixRoom: MatrixRoom,
  options: UseCallRecordingOptions = {},
): UseCallRecordingResult {
  const cli = useClient();
  const {
    onFinished,
    onRecordingNotice,
    mimeType = "video/webm;codecs=vp9,opus",
  } = options;
  const participants = useParticipants({ room });

  const onFinishedRef = useRef<UseCallRecordingOptions["onFinished"]>(null);
  onFinishedRef.current = onFinished;

  const onRecordingNoticeRef = useRef<UseCallRecordingOptions["onRecordingNotice"]>(
    null,
  );
  onRecordingNoticeRef.current = onRecordingNotice;

  const [localRecordingState, setLocalRecordingState] = useState<LocalRecordingState>(
    "stopped",
  );
  const [localRecordingDeviceKeys, setLocalRecordingDeviceKeys] = useState<Set<string>>(
    new Set(),
  );
  const [error, setError] = useState<Error | null>(null);

  const isLocalRecording = localRecordingState === "recording";
  const isLocalPreparing = localRecordingState === "preparing";
  const isAnyRecording = isLocalRecording;

  const currentUserId = cli.client?.getUserId() ?? null;
  const currentDeviceId = cli.client?.getDeviceId() ?? null;

  const recorderRef = useRef<MediaRecorder | null>(null);
  const tabStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const destinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);

  const processedRecordingEventsRef = useRef<Set<string>>(new Set());
  const latestJoinTsByDeviceKeyRef = useRef<Map<string, number>>(new Map());
  const activeDeviceIdsByUserRef = useRef<Map<string, Set<string>>>(new Map());
  const localTimelineSyncGenerationRef = useRef(0);

  // key = `${participantSid}:${trackSid}`
  const trackSourcesRef = useRef<Map<string, TrackSourceEntry>>(new Map());

  // key = participantSid
  const participantHandlersRef = useRef<
    Map<string, { participant: Participant; handlers: ParticipantHandlers }>
  >(new Map());

  const makeKey = (participantSid: string, trackSid: string): string =>
    `${participantSid}:${trackSid}`;

  const inCallUserIds = useMemo(() => {
    const users = new Set<string>();

    if (currentUserId) {
      users.add(currentUserId);
    }

    participants.forEach((participant) => {
      const userId = getUserIdFromParticipantIdentity(participant.identity);
      if (userId) {
        users.add(userId);
      }
    });

    return users;
  }, [participants, currentUserId]);

  const inCallUserIdsSignature = useMemo(
    () => Array.from(inCallUserIds).sort().join("|"),
    [inCallUserIds],
  );

  const emitRecordingNotice = useCallback((notice: RecordingNotice): void => {
    onRecordingNoticeRef.current?.(notice);
  }, []);

  const setDeviceLocalRecording = useCallback(
    (userId: string | null, deviceId: string | null, isRecordingNow: boolean): void => {
      if (!userId || !deviceId) {
        return;
      }

      const deviceKey = makeUserDeviceKey(userId, deviceId);
      setLocalRecordingDeviceKeys((prev) => {
        const next = new Set(prev);
        if (isRecordingNow) {
          next.add(deviceKey);
        } else {
          next.delete(deviceKey);
        }
        return next;
      });
    },
    [],
  );

  const setUserLocalRecordingFallback = useCallback(
    (userId: string | null, isRecordingNow: boolean): void => {
      if (!userId) {
        return;
      }

      const activeDevices = activeDeviceIdsByUserRef.current.get(userId);
      if (activeDevices && activeDevices.size > 0) {
        activeDevices.forEach((deviceId) => {
          setDeviceLocalRecording(userId, deviceId, isRecordingNow);
        });
        return;
      }

      // Для старых событий без deviceId используем синтетический девайс.
      setDeviceLocalRecording(userId, UNKNOWN_DEVICE_ID, isRecordingNow);
    },
    [setDeviceLocalRecording],
  );

  const resolveActiveCallBoundaries = useCallback((): ActiveCallBoundaries => {
    const result: ActiveCallBoundaries = {
      callStartedAtTs: null,
      deviceJoinedAtTsByKey: new Map<string, number>(),
      activeDeviceIdsByUserId: new Map<string, Set<string>>(),
    };

    const roomState = matrixRoom.getLiveTimeline().getState(EventTimeline.FORWARDS);
    if (!roomState) {
      return result;
    }

    const membershipEvents = roomState.getStateEvents(CALL_MEMBERSHIP_EVENT_TYPE);
    for (const event of membershipEvents) {
      const sender = event.getSender();
      if (!sender) {
        continue;
      }

      const joinedAtTs = parseCallMembershipCreatedTs(event);
      if (joinedAtTs === null) {
        continue;
      }

      if (result.callStartedAtTs === null || joinedAtTs < result.callStartedAtTs) {
        result.callStartedAtTs = joinedAtTs;
      }

      if (!inCallUserIds.has(sender)) {
        continue;
      }

      const deviceId = parseCallMembershipDeviceId(event);
      if (!deviceId) {
        continue;
      }

      const deviceKey = makeUserDeviceKey(sender, deviceId);
      const previousJoinedAtTs = result.deviceJoinedAtTsByKey.get(deviceKey);
      if (previousJoinedAtTs === undefined || joinedAtTs > previousJoinedAtTs) {
        result.deviceJoinedAtTsByKey.set(deviceKey, joinedAtTs);
      }

      const knownDeviceIds = result.activeDeviceIdsByUserId.get(sender) ?? new Set();
      knownDeviceIds.add(deviceId);
      result.activeDeviceIdsByUserId.set(sender, knownDeviceIds);
    }

    return result;
  }, [inCallUserIds, matrixRoom]);

  const sendRecordingChangeEvent = useCallback(
    async ({
      action,
      deviceId,
    }: {
      action: RecordingChangeAction;
      deviceId?: string | null;
    }): Promise<void> => {
      const matrixClient = cli.client;
      if (!matrixClient) {
        return;
      }

      const roomCallId =
        parseNonEmptyString(room.name) ??
        parseNonEmptyString(await room.getSid().catch(() => null));
      if (!roomCallId) {
        logger.warn("[Recorder] Не удалось определить callId для события записи");
        return;
      }

      const content: RecordingChangeEventContent = {
        action,
        type: "local",
        callId: roomCallId,
        deviceId: deviceId ?? null,
      };

      try {
        await matrixClient.sendEvent(
          matrixRoom.roomId,
          RecordingChangeEventType,
          content,
        );
      } catch (err) {
        logger.warn("[Recorder] Не удалось отправить событие записи", err);
      }
    },
    [cli.client, matrixRoom.roomId, room],
  );

  const readLocalRecordingDevicesFromTimeline = useCallback(
    async (
      boundaries: ActiveCallBoundaries = resolveActiveCallBoundaries(),
    ): Promise<Set<string>> => {
      const matrixClient = cli.client;
      if (!matrixClient) {
        return new Set();
      }

      const { callStartedAtTs, deviceJoinedAtTsByKey, activeDeviceIdsByUserId } =
        boundaries;
      if (deviceJoinedAtTsByKey.size === 0) {
        return new Set();
      }

      const unresolvedDeviceKeys = new Set(deviceJoinedAtTsByKey.keys());
      const recordingDeviceKeys = new Set<string>();
      const seenEventIds = new Set<string>();

      let scannedPages = 0;
      while (
        scannedPages <= EVENT_SCAN_MAX_PAGES &&
        unresolvedDeviceKeys.size > 0
      ) {
        const events = matrixRoom.getLiveTimeline().getEvents();
        let shouldStopScanning = false;

        for (let i = events.length - 1; i >= 0; i -= 1) {
          const event = events[i];
          const eventId = event.getId();
          if (eventId && seenEventIds.has(eventId)) {
            continue;
          }
          if (eventId) {
            seenEventIds.add(eventId);
          }

          if (event.getRoomId() !== matrixRoom.roomId || event.isSending()) {
            continue;
          }

          if (callStartedAtTs !== null && event.getTs() < callStartedAtTs) {
            shouldStopScanning = true;
            break;
          }

          await matrixClient.decryptEventIfNeeded(event).catch(() => {});

          if (event.isBeingDecrypted() || event.isDecryptionFailure()) {
            continue;
          }

          if (event.getType() !== RecordingChangeEventType) {
            continue;
          }

          const content = event.getContent();
          if (!isRecordingChangeEventContent(content)) {
            continue;
          }

          const sender = event.getSender();
          if (!sender) {
            continue;
          }

          const eventDeviceId = parseNonEmptyString(content.deviceId);
          if (eventDeviceId) {
            const eventDeviceKey = makeUserDeviceKey(sender, eventDeviceId);
            if (!unresolvedDeviceKeys.has(eventDeviceKey)) {
              continue;
            }

            const joinedAtTs = deviceJoinedAtTsByKey.get(eventDeviceKey);
            if (joinedAtTs === undefined || event.getTs() < joinedAtTs) {
              continue;
            }

            if (content.action === "start") {
              recordingDeviceKeys.add(eventDeviceKey);
            } else {
              recordingDeviceKeys.delete(eventDeviceKey);
            }

            unresolvedDeviceKeys.delete(eventDeviceKey);
            continue;
          }

          const senderActiveDeviceIds = activeDeviceIdsByUserId.get(sender);
          if (!senderActiveDeviceIds || senderActiveDeviceIds.size === 0) {
            continue;
          }

          senderActiveDeviceIds.forEach((deviceId) => {
            const eventDeviceKey = makeUserDeviceKey(sender, deviceId);
            if (!unresolvedDeviceKeys.has(eventDeviceKey)) {
              return;
            }

            const joinedAtTs = deviceJoinedAtTsByKey.get(eventDeviceKey);
            if (joinedAtTs === undefined || event.getTs() < joinedAtTs) {
              return;
            }

            if (content.action === "start") {
              recordingDeviceKeys.add(eventDeviceKey);
            } else {
              recordingDeviceKeys.delete(eventDeviceKey);
            }

            unresolvedDeviceKeys.delete(eventDeviceKey);
          });
        }

        if (shouldStopScanning) {
          break;
        }

        const canPaginate =
          !!matrixRoom
            .getLiveTimeline()
            .getState(EventTimeline.BACKWARDS)?.paginationToken;

        if (!canPaginate) {
          break;
        }

        scannedPages += 1;

        try {
          await matrixClient.scrollback(matrixRoom, EVENT_SCAN_PAGE_SIZE);
        } catch (err) {
          logger.warn(
            "[Recorder] Не удалось догрузить таймлайн для локального fallback",
            err,
          );
          break;
        }
      }

      return recordingDeviceKeys;
    },
    [cli.client, matrixRoom, resolveActiveCallBoundaries],
  );

  const detachTrack = useCallback((participantSid: string, trackSid: string) => {
    const key = makeKey(participantSid, trackSid);
    const entry = trackSourcesRef.current.get(key);
    if (!entry) return;

    try {
      entry.source.disconnect();
    } catch {
      // ignore
    }

    trackSourcesRef.current.delete(key);
  }, []);

  const attachTrack = useCallback(
    (participant: Participant, publication: TrackPublication) => {
      const ctx = audioContextRef.current;
      const dest = destinationRef.current;
      if (!ctx || !dest) return;

      const track = publication.track;
      if (!track?.mediaStreamTrack) return;
      if (track.mediaStreamTrack.kind !== "audio") return;
      if (publication.isMuted) return;

      const key = makeKey(participant.sid, publication.trackSid);

      // Если уже был источник, отключаем его, чтобы не получить дубли в миксе.
      const existing = trackSourcesRef.current.get(key);
      if (existing) {
        try {
          existing.source.disconnect();
        } catch {
          // ignore
        }
        trackSourcesRef.current.delete(key);
      }

      const stream = new MediaStream([track.mediaStreamTrack]);
      const source = ctx.createMediaStreamSource(stream);
      source.connect(dest);

      trackSourcesRef.current.set(key, { source, stream });
    },
    [],
  );

  const detachAllTracksForParticipant = useCallback(
    (participantSid: string) => {
      const keys = Array.from(trackSourcesRef.current.keys()).filter((k) =>
        k.startsWith(`${participantSid}:`),
      );
      keys.forEach((k) => {
        const entry = trackSourcesRef.current.get(k);
        if (!entry) return;

        try {
          entry.source.disconnect();
        } catch {
          // ignore
        }

        trackSourcesRef.current.delete(k);
      });
    },
    [],
  );

  const bindParticipantEvents = useCallback(
    (participant: Participant) => {
      if (participantHandlersRef.current.has(participant.sid)) return;

      const handlers: ParticipantHandlers = {
        trackSubscribed: (_track: RemoteTrack, pub: RemoteTrackPublication) => {
          attachTrack(participant, pub);
        },
        trackUnsubscribed: (_track: RemoteTrack, pub: RemoteTrackPublication) => {
          detachTrack(participant.sid, pub.trackSid);
        },
        trackMuted: (pub: TrackPublication) => {
          detachTrack(participant.sid, pub.trackSid);
        },
        trackUnmuted: (pub: TrackPublication) => {
          attachTrack(participant, pub);
        },
      };

      participant.on(ParticipantEvent.TrackSubscribed, handlers.trackSubscribed);
      participant.on(ParticipantEvent.TrackUnsubscribed, handlers.trackUnsubscribed);
      participant.on(ParticipantEvent.TrackMuted, handlers.trackMuted);
      participant.on(ParticipantEvent.TrackUnmuted, handlers.trackUnmuted);

      participantHandlersRef.current.set(participant.sid, { participant, handlers });
    },
    [attachTrack, detachTrack],
  );

  const unbindParticipantEvents = useCallback((sid: string) => {
    const entry = participantHandlersRef.current.get(sid);
    if (!entry) return;

    const { participant, handlers } = entry;

    participant.off(ParticipantEvent.TrackSubscribed, handlers.trackSubscribed);
    participant.off(ParticipantEvent.TrackUnsubscribed, handlers.trackUnsubscribed);
    participant.off(ParticipantEvent.TrackMuted, handlers.trackMuted);
    participant.off(ParticipantEvent.TrackUnmuted, handlers.trackUnmuted);

    participantHandlersRef.current.delete(sid);
  }, []);

  const attachExistingTracks = useCallback(
    (participant: Participant) => {
      participant.audioTrackPublications.forEach((pub) => {
        attachTrack(participant, pub as TrackPublication);
      });
    },
    [attachTrack],
  );

  const cleanup = useCallback(() => {
    recorderRef.current = null;
    chunksRef.current = [];

    tabStreamRef.current?.getTracks().forEach((track) => track.stop());
    tabStreamRef.current = null;

    trackSourcesRef.current.forEach((entry) => {
      try {
        entry.source.disconnect();
      } catch {
        // ignore
      }
    });
    trackSourcesRef.current.clear();

    participantHandlersRef.current.forEach((_entry, sid) => {
      unbindParticipantEvents(sid);
      detachAllTracksForParticipant(sid);
    });
    participantHandlersRef.current.clear();

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    destinationRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopLocalRecording = useCallback(async (): Promise<void> => {
    if (localRecordingState !== "recording") {
      return Promise.resolve();
    }

    const recorder = recorderRef.current;
    if (!recorder) {
      return Promise.resolve();
    }

    if (recorder.state === "inactive") {
      return Promise.resolve();
    }

    try {
      recorder.stop();
    } catch {
      // ignore
    }
  }, [localRecordingState]);

  const startLocalRecording = useCallback(async (): Promise<void> => {
    if (localRecordingState !== "stopped") {
      return;
    }

    setError(null);
    setLocalRecordingState("preparing");

    try {
      const tabStream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: "browser" },
        audio: false,
        // @ts-expect-error не в спеке
        preferCurrentTab: true,
        surfaceSwitching: "exclude",
        monitorTypeSurfaces: "exclude",
        systemAudio: "exclude",
        selfBrowserSurface: "include",
      });
      tabStreamRef.current = tabStream;

      const ctx = new AudioContext();
      const dest = ctx.createMediaStreamDestination();
      audioContextRef.current = ctx;
      destinationRef.current = dest;

      const mixedStream = new MediaStream([
        ...tabStream.getVideoTracks(),
        ...dest.stream.getAudioTracks(),
      ]);

      const supportedMimeType = MediaRecorder.isTypeSupported(mimeType)
        ? mimeType
        : "video/webm";

      const recorder = new MediaRecorder(mixedStream, {
        mimeType: supportedMimeType,
      });
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e: BlobEvent): void => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onerror = (e): void => {
        setError(e.error || new Error("Recording error"));
        setLocalRecordingState("stopped");
        setDeviceLocalRecording(
          currentUserId,
          currentDeviceId ?? UNKNOWN_DEVICE_ID,
          false,
        );
        cleanup();
      };

      recorder.onstop = (): void => {
        const blob = new Blob(chunksRef.current, { type: supportedMimeType });
        chunksRef.current = [];
        cleanup();
        setLocalRecordingState("stopped");
        setDeviceLocalRecording(
          currentUserId,
          currentDeviceId ?? UNKNOWN_DEVICE_ID,
          false,
        );

        void sendRecordingChangeEvent({
          action: "stop",
          deviceId: currentDeviceId,
        });

        if (blob.size > 0 && onFinishedRef.current) {
          onFinishedRef.current(blob);
        }
      };

      participants.forEach((participant) => {
        attachExistingTracks(participant);
        bindParticipantEvents(participant);
      });

      recorder.start(1000);
      setLocalRecordingState("recording");
      setDeviceLocalRecording(
        currentUserId,
        currentDeviceId ?? UNKNOWN_DEVICE_ID,
        true,
      );

      await sendRecordingChangeEvent({
        action: "start",
        deviceId: currentDeviceId,
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to start recording"));
      setLocalRecordingState("stopped");
      setDeviceLocalRecording(
        currentUserId,
        currentDeviceId ?? UNKNOWN_DEVICE_ID,
        false,
      );
      cleanup();
    }
  }, [
    attachExistingTracks,
    bindParticipantEvents,
    cleanup,
    currentDeviceId,
    currentUserId,
    localRecordingState,
    mimeType,
    participants,
    sendRecordingChangeEvent,
    setDeviceLocalRecording,
  ]);

  const handleRecordingChangeEvent = useCallback(
    async (event: MatrixEvent): Promise<void> => {
      if (event.getRoomId() !== matrixRoom.roomId || event.isSending()) {
        return;
      }

      const eventId = event.getId();
      if (eventId && processedRecordingEventsRef.current.has(eventId)) {
        return;
      }

      const matrixClient = cli.client;
      if (!matrixClient) {
        return;
      }

      await matrixClient.decryptEventIfNeeded(event).catch(() => {});
      if (event.isBeingDecrypted() || event.isDecryptionFailure()) {
        return;
      }

      const eventType = event.getType();

      if (eventType === CALL_MEMBERSHIP_EVENT_TYPE) {
        const sender = event.getSender();
        if (!sender) {
          return;
        }

        const membershipDeviceId = parseCallMembershipDeviceId(event);
        if (!membershipDeviceId) {
          return;
        }

        const deviceKey = makeUserDeviceKey(sender, membershipDeviceId);
        const joinedAtTs = parseCallMembershipCreatedTs(event);
        if (joinedAtTs === null) {
          latestJoinTsByDeviceKeyRef.current.delete(deviceKey);

          const activeDeviceIds = activeDeviceIdsByUserRef.current.get(sender);
          if (activeDeviceIds) {
            activeDeviceIds.delete(membershipDeviceId);
            if (activeDeviceIds.size === 0) {
              activeDeviceIdsByUserRef.current.delete(sender);
            } else {
              activeDeviceIdsByUserRef.current.set(sender, activeDeviceIds);
            }
          }

          setDeviceLocalRecording(sender, membershipDeviceId, false);
          if (!activeDeviceIdsByUserRef.current.has(sender)) {
            setDeviceLocalRecording(sender, UNKNOWN_DEVICE_ID, false);
          }
          return;
        }

        const activeDeviceIds = activeDeviceIdsByUserRef.current.get(sender) ?? new Set();
        activeDeviceIds.add(membershipDeviceId);
        activeDeviceIdsByUserRef.current.set(sender, activeDeviceIds);

        const previousJoinedAtTs = latestJoinTsByDeviceKeyRef.current.get(deviceKey);
        if (previousJoinedAtTs === undefined || joinedAtTs > previousJoinedAtTs) {
          latestJoinTsByDeviceKeyRef.current.set(deviceKey, joinedAtTs);
          setDeviceLocalRecording(sender, membershipDeviceId, false);
          setDeviceLocalRecording(sender, UNKNOWN_DEVICE_ID, false);
        }

        return;
      }

      if (eventType !== RecordingChangeEventType) {
        return;
      }

      const content = event.getContent();
      if (!isRecordingChangeEventContent(content)) {
        return;
      }

      if (eventId) {
        processedRecordingEventsRef.current.add(eventId);
        if (processedRecordingEventsRef.current.size > 500) {
          processedRecordingEventsRef.current.clear();
        }
      }

      const sender = event.getSender() ?? null;
      const eventDeviceId = parseNonEmptyString(content.deviceId);
      const isStart = content.action === "start";

      if (content.action === "start") {
        emitRecordingNotice({ kind: "local_start", sender });
      } else {
        emitRecordingNotice({ kind: "local_stop", sender });
      }

      if (eventDeviceId) {
        setDeviceLocalRecording(sender, eventDeviceId, isStart);
      } else {
        setUserLocalRecordingFallback(sender, isStart);
      }
    },
    [
      cli.client,
      emitRecordingNotice,
      matrixRoom.roomId,
      setDeviceLocalRecording,
      setUserLocalRecordingFallback,
    ],
  );

  useEffect(() => {
    let cancelled = false;
    const generation = localTimelineSyncGenerationRef.current + 1;
    localTimelineSyncGenerationRef.current = generation;

    const boundaries = resolveActiveCallBoundaries();
    latestJoinTsByDeviceKeyRef.current = new Map(boundaries.deviceJoinedAtTsByKey);
    activeDeviceIdsByUserRef.current = new Map(
      Array.from(boundaries.activeDeviceIdsByUserId.entries()).map(
        ([userId, deviceIds]) => [userId, new Set(deviceIds)],
      ),
    );

    void readLocalRecordingDevicesFromTimeline(boundaries)
      .then((recordingDeviceKeys) => {
        if (cancelled || generation !== localTimelineSyncGenerationRef.current) {
          return;
        }
        if (isLocalRecording && currentUserId) {
          recordingDeviceKeys.add(
            makeUserDeviceKey(
              currentUserId,
              currentDeviceId ?? UNKNOWN_DEVICE_ID,
            ),
          );
        }
        setLocalRecordingDeviceKeys(recordingDeviceKeys);
      })
      .catch((err) => {
        logger.warn("[Recorder] Не удалось восстановить локальные записи из таймлайна", err);
      });

    return (): void => {
      cancelled = true;
    };
  }, [
    currentDeviceId,
    currentUserId,
    inCallUserIdsSignature,
    isLocalRecording,
    readLocalRecordingDevicesFromTimeline,
    resolveActiveCallBoundaries,
  ]);

  useEffect(() => {
    const matrixClient = cli.client;
    if (!matrixClient) {
      return;
    }

    const onTimelineEvent = (
      event: MatrixEvent,
      _room?: MatrixRoom,
      toStartOfTimeline?: boolean,
    ): void => {
      if (toStartOfTimeline) {
        return;
      }
      void handleRecordingChangeEvent(event);
    };

    const onDecryptedEvent = (event: MatrixEvent): void => {
      void handleRecordingChangeEvent(event);
    };

    matrixRoom.on(MatrixRoomEvent.Timeline, onTimelineEvent);
    matrixClient.on(MatrixEventEvent.Decrypted, onDecryptedEvent);

    return (): void => {
      matrixRoom.off(MatrixRoomEvent.Timeline, onTimelineEvent);
      matrixClient.off(MatrixEventEvent.Decrypted, onDecryptedEvent);
    };
  }, [cli.client, handleRecordingChangeEvent, matrixRoom]);

  useEffect(() => {
    if (!isLocalRecording) return;

    participants.forEach((participant) => {
      if (!participantHandlersRef.current.has(participant.sid)) {
        attachExistingTracks(participant);
        bindParticipantEvents(participant);
      }
    });

    participantHandlersRef.current.forEach((_entry, sid) => {
      const stillHere = participants.some((participant) => participant.sid === sid);
      if (!stillHere) {
        unbindParticipantEvents(sid);
        detachAllTracksForParticipant(sid);
      }
    });
  }, [
    participants,
    isLocalRecording,
    attachExistingTracks,
    bindParticipantEvents,
    detachAllTracksForParticipant,
    unbindParticipantEvents,
  ]);

  const stopLocalRecordingRef = useRef(stopLocalRecording);
  stopLocalRecordingRef.current = stopLocalRecording;

  useEffect(() => {
    return (): void => {
      void stopLocalRecordingRef.current();
      cleanup();
    };
    // Функция cleanup не меняется, эффект нужен только на unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLocalRecording) {
      return;
    }

    const close = (): void => {
      void stopLocalRecordingRef.current();
      cleanup();
    };

    window.addEventListener("beforeunload", close);
    return (): void => {
      window.removeEventListener("beforeunload", close);
    };
    // При смене статуса локальной записи переустанавливаем обработчик.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocalRecording]);

  const localRecordingUserIds = useMemo(() => {
    const userIds = new Set<string>();
    localRecordingDeviceKeys.forEach((deviceKey) => {
      userIds.add(parseUserIdFromDeviceKey(deviceKey));
    });
    return userIds;
  }, [localRecordingDeviceKeys]);

  const localRecordingUserIdsRef = useRef<ReadonlySet<string>>(new Set());
  localRecordingUserIdsRef.current = localRecordingUserIds;

  const isUserLocallyRecording = useCallback(
    (userId: string | null | undefined): boolean =>
      !!userId && localRecordingUserIdsRef.current.has(userId),
    [],
  );

  return {
    localRecordingState,
    localRecordingUserIds,
    isLocalRecording,
    isLocalPreparing,
    isAnyRecording,
    isUserLocallyRecording,
    error,
    startLocalRecording,
    stopLocalRecording,
  };
}
